// utils/dateUtils.js

/**
 * Centralized date formatting utilities for DiNiTracker
 *
 * IMPORTANT NOTES:
 * - Server stores dates in UTC, 
 *      EXPECT IN tbl_Wig_Order_Room_Tasks.scanOutDate,scanInDate, wigtransferdate which are in user inputformat 
 *     and tbl_Wig_Order_Room_Tasks.EntryDate which is also in user input format
 * - US users (Pomona, Lakewood) are in EST (America/New_York)
 * - Israel users are in Asia/Jerusalem timezone
 * - API expects ISO format: YYYY-MM-DD or YYYY-MM-DDTHH:mm
 */

// ==================== TIMEZONE HELPERS ====================

import { isIsraelUser } from "./locationUtils";

/**
 * Get timezone for user
 * @param {string} username - Username
 * @returns {string} IANA timezone identifier
 */
export const getUserTimezone = (username) => {
  return isIsraelUser(username) ? 'Asia/Jerusalem' : 'America/New_York';
};


/**
 * Format date with timezone awareness based on user location
 * 4/30/26 22:15/
 * @param {Date|string} date - Date to format
 * @param {string} username - Username to determine timezone
 * @returns {string} Formatted date and time string
 */
export const formatDateTimeForUser = (date, username) => {
  const d = date instanceof Date ? date : new Date(date);
  if (!isValidDate(d)) return 'Invalid Date';

  const timezone = getUserTimezone(username);
  const isIsraeli = isIsraelUser(username);

  try {
    const dateStr = d.toLocaleDateString('en-US', {
      timeZone: timezone,
      year: '2-digit',//numeric for 4-digit year, 2-digit for 2-digit year
      month: 'numeric',
      day: 'numeric'
    });

    const timeStr = d.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: isIsraeli ? '2-digit' : 'numeric',  // US: no leading zero (3:45), Israeli: leading zero (03:45)
      minute: '2-digit',
      hour12: !isIsraeli  // 24-hour format for Israeli users, AM/PM for US users
    });

    return `${dateStr} ${timeStr}`;
  } catch (error) {
    console.error('Error formatting date with timezone:', error);
    // Fallback to local time
    return formatDateTime(d);
  }
};

export const formatCurrency = (amount) => {
    const num = parseFloat(amount || 0);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };


// ==================== VALIDATION ====================

/**
 * Check if a date is valid
 * @param {Date|string|number} date - Date to validate
 * @returns {boolean} True if valid date
 */
export const isValidDate = (date) => {
  if (!date) return false;
  const d = date instanceof Date ? date : new Date(date);
  return !isNaN(d.getTime());
};

/**
 * Parse a date string safely
 * @param {string} dateString - Date string to parse
 * @returns {Date|null} Parsed date or null if invalid
 */
export const parseDate = (dateString) => {
  if (!dateString) return null;

  // Add :00 seconds if missing (for datetime-local inputs)
  const dateStr = dateString.includes(':00') ? dateString : dateString + ':00';
  const date = new Date(dateStr);

  return isValidDate(date) ? date : null;
};

// ==================== API FORMATS (for server communication) ====================

/**
 * Format date for API calls (YYYY-MM-DD)
 * @param {Date|string} date - Date to format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const toAPIDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (!isValidDate(d)) return '';

  // Use local calendar components, not toISOString() (which converts to UTC first).
  // toISOString() rolls the date forward once local time + UTC offset crosses
  // midnight UTC - e.g. for US Eastern (UTC-4/-5), any time from ~8pm onward would
  // report tomorrow's date instead of today's for callers passing `new Date()`.
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format datetime for API calls (YYYY-MM-DDTHH:mm:ss)
 * @param {Date|string} date - Date to format
 * @returns {string} DateTime in ISO format
 */
export const toAPIDateTime = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (!isValidDate(d)) return '';

  return d.toISOString();
};

/**
 * Format local datetime for datetime-local inputs (YYYY-MM-DDTHH:mm)
 * @param {Date|string} date - Date to format
 * @returns {string} DateTime in local format without timezone
 */
export const toLocalDateTime = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (!isValidDate(d)) return '';

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Convert a local datetime-local input value to UTC for database storage
 * @param {string} localDateTimeString - Value from datetime-local input (YYYY-MM-DDTHH:mm)
 * @returns {string} UTC ISO string for database
 */
export const localDateTimeToUTC = (localDateTimeString) => {
  if (!localDateTimeString) return null;
  
  // Create date from local input (browser treats this as local time)
  const localDate = new Date(localDateTimeString);
  
  // Return as UTC ISO string for database
  return localDate.toISOString();
};

/**
 * Convert UTC date from database to local datetime-local format for inputs
 * @param {string} utcDateString - UTC date string from database
 * @returns {string} Local datetime string for datetime-local inputs
 */
export const utcToLocalDateTime = (utcDateString) => {
  if (!utcDateString) return '';
  
  // Parse UTC date
  const utcDate = new Date(utcDateString);
  
  // Convert to local time for display in datetime-local input
  const year = utcDate.getFullYear();
  const month = String(utcDate.getMonth() + 1).padStart(2, '0');
  const day = String(utcDate.getDate()).padStart(2, '0');
  const hours = String(utcDate.getHours()).padStart(2, '0');
  const minutes = String(utcDate.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Convert UTC date from database to local date for date inputs (YYYY-MM-DD)
 * @param {string} utcDateString - UTC date string from database
 * @returns {string} Local date string for date inputs
 */
export const utcToLocalDate = (utcDateString) => {
  if (!utcDateString) return '';
  
  const utcDate = new Date(utcDateString);
  const year = utcDate.getFullYear();
  const month = String(utcDate.getMonth() + 1).padStart(2, '0');
  const day = String(utcDate.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

// ==================== DISPLAY FORMATS (for UI) ====================

/**
 * Format date for display (e.g., "Jan 15, 2025")
 * Handles timezone-safe date parsing for date-only values
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return 'Invalid Date';

  // If it's a YYYY-MM-DD string, parse it as local date to avoid timezone shift
  if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}/)) {
    const [year, month, day] = date.split('T')[0].split('-');
    const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

    return localDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  // Otherwise handle as Date object
  const d = date instanceof Date ? date : new Date(date);
  if (!isValidDate(d)) return 'Invalid Date';

  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format date with long month name (e.g., "January 15, 2025")
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatLongDate = (date) => {
  let d;
  if (date instanceof Date) {
    d = date;
  } else if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    // Parse date-only strings as local time, not UTC
    const [year, month, day] = date.split('-').map(Number);
    d = new Date(year, month - 1, day);
  } else {
    d = new Date(date);
  }

  if (!isValidDate(d)) return 'Invalid Date';

  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};


/**
 * Format time in 12-hour format (e.g., "2:30 PM")
 * @param {Date|string} date - Date to format
 * @param {boolean} useMilitaryTime - If true, use 24-hour format
 * @returns {string} Formatted time string
 */
export const formatTime = (date, useMilitaryTime = false) => {
  const d = date instanceof Date ? date : new Date(date);
  if (!isValidDate(d)) return 'Invalid Time';

  if (useMilitaryTime) {
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }

  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format datetime for display (e.g., "Jan 15, 2025 2:30 PM" or "Jan 15, 2025 14:30")
 * @param {Date|string} date - Date to format
 * @param {boolean} useMilitaryTime - If true, use 24-hour format
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (date, useMilitaryTime = false) => {
  const d = date instanceof Date ? date : new Date(date);
  if (!isValidDate(d)) return 'Invalid Date';

  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: !useMilitaryTime
  });
};
/**
 * Format date as "Weekday, Mon DD, YYYY" (e.g., "Wed, Jan 15, 2025")
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string with weekday
 */ 
export const formatDateWithWeekday = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (!isValidDate(d)) return 'Invalid Date';

  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Format date as M/D/YY (e.g., "1/15/25")
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatShortDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (!isValidDate(d)) return '';

  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
};


// ==================== COMPARISONS ====================

/**
 * Check if two dates are on the same day
 * @param {Date|string} date1 - First date
 * @param {Date|string} date2 - Second date
 * @returns {boolean} True if same day
 */
export const isSameDay = (date1, date2) => {
  const d1 = date1 instanceof Date ? date1 : new Date(date1);
  const d2 = date2 instanceof Date ? date2 : new Date(date2);

  return d1.toDateString() === d2.toDateString();
};

/**
 * Check if a date is today
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => {
  return isSameDay(date, new Date());
};

/**
 * Check if date is in the past
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is in the past
 */
export const isPast = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return d < new Date();
};

/**
 * Check if date is in the future
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is in the future
 */
export const isFuture = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return d > new Date();
};

// ==================== CALCULATIONS ====================

/**
 * Add days to a date
 * @param {Date|string} date - Starting date
 * @param {number} days - Number of days to add (can be negative)
 * @returns {Date} New date
 */
export const addDays = (date, days) => {
  const d = new Date(date instanceof Date ? date : new Date(date));
  d.setDate(d.getDate() + days);
  return d;
};

/**
 * Add hours to a date
 * @param {Date|string} date - Starting date
 * @param {number} hours - Number of hours to add (can be negative)
 * @returns {Date} New date
 */
export const addHours = (date, hours) => {
  const d = new Date(date instanceof Date ? date : new Date(date));
  d.setHours(d.getHours() + hours);
  return d;
};

/**
 * Add minutes to a date
 * @param {Date|string} date - Starting date
 * @param {number} minutes - Number of minutes to add (can be negative)
 * @returns {Date} New date
 */
export const addMinutes = (date, minutes) => {
  const d = new Date(date instanceof Date ? date : new Date(date));
  d.setMinutes(d.getMinutes() + minutes);
  return d;
};

/**
 * Add months to a date
 * @param {Date|string} date - Starting date
 * @param {number} months - Number of months to add (can be negative)
 * @returns {Date} New date
 */
export const addMonths = (date, months) => {
  const d = new Date(date instanceof Date ? date : new Date(date));
  d.setMonth(d.getMonth() + months);
  return d;
};

/**
 * Calculate duration between two dates in minutes
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {number} Duration in minutes
 */
export const getDurationMinutes = (startDate, endDate) => {
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  return Math.round((end - start) / (1000 * 60));
};

/**
 * Format duration as human readable string (e.g., "1h 30m")
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration
 */
export const formatDuration = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) {
    return `${mins}m`;
  } else if (mins === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${mins}m`;
  }
};

// ==================== TIME PARSING ====================

/**
 * Parse 12-hour time string to 24-hour format
 * @param {string} timeString - Time string like "2:00 PM"
 * @returns {object} Object with hours and minutes in 24-hour format
 */
export const parseTimeSlot = (timeString) => {
  const [time, ampm] = timeString.split(' ');
  const [hours, minutes] = time.split(':');
  let hour24 = parseInt(hours);

  // Convert to 24-hour format
  if (ampm === 'PM' && hour24 !== 12) {
    hour24 += 12;
  } else if (ampm === 'AM' && hour24 === 12) {
    hour24 = 0;
  }

  return {
    hours: hour24,
    minutes: parseInt(minutes)
  };
};

/**
 * Create a date from a base date and time slot
 * @param {Date|string} baseDate - Base date
 * @param {string} timeSlot - Time slot like "2:00 PM"
 * @returns {Date} New date with time set
 */
export const combineDateAndTimeSlot = (baseDate, timeSlot) => {
  const d = new Date(baseDate instanceof Date ? baseDate : new Date(baseDate));
  const { hours, minutes } = parseTimeSlot(timeSlot);

  d.setHours(hours, minutes, 0, 0);
  return d;
};

// ==================== DATE RANGE HELPERS ====================

/**
 * Get start of day (midnight)
 * @param {Date|string} date - Date
 * @returns {Date} Date at 00:00:00
 */
export const startOfDay = (date) => {
  const d = new Date(date instanceof Date ? date : new Date(date));
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Get end of day (23:59:59.999)
 * @param {Date|string} date - Date
 * @returns {Date} Date at 23:59:59.999
 */
export const endOfDay = (date) => {
  const d = new Date(date instanceof Date ? date : new Date(date));
  d.setHours(23, 59, 59, 999);
  return d;
};

/**
 * Get start of week (Sunday)
 * @param {Date|string} date - Date
 * @returns {Date} Sunday of that week
 */
export const startOfWeek = (date) => {
  const d = new Date(date instanceof Date ? date : new Date(date));
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Get end of week (Saturday)
 * @param {Date|string} date - Date
 * @returns {Date} Saturday of that week
 */
export const endOfWeek = (date) => {
  const d = new Date(date instanceof Date ? date : new Date(date));
  const day = d.getDay();
  d.setDate(d.getDate() + (6 - day));
  d.setHours(23, 59, 59, 999);
  return d;
};

/**
 * Get start of month
 * @param {Date|string} date - Date
 * @returns {Date} First day of month
 */
export const startOfMonth = (date) => {
  const d = new Date(date instanceof Date ? date : new Date(date));
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Get end of month
 * @param {Date|string} date - Date
 * @returns {Date} Last day of month
 */
export const endOfMonth = (date) => {
  const d = new Date(date instanceof Date ? date : new Date(date));
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  d.setHours(23, 59, 59, 999);
  return d;
};

/**
 * Generate array of dates for a week
 * @param {Date|string} date - Any date in the week
 * @returns {Date[]} Array of 7 dates (Sun-Sat)
 */
export const getWeekDays = (date) => {
  const start = startOfWeek(date);
  const days = [];

  for (let i = 0; i < 7; i++) {
    days.push(addDays(start, i));
  }

  return days;
};

// ==================== SPECIALIZED FORMATS ====================

/**
 * Format date for file names (YYYY-MM-DD)
 * @param {Date|string} date - Date to format
 * @returns {string} Date formatted for file names
 */
export const toFileNameDate = (date) => {
  return toAPIDate(date);
};

/**
 * Format current timestamp for logs --UTC Time
 * @returns {string} ISO timestamp
 */
export const getCurrentTimestamp = () => {
  return new Date().toISOString();
};



 export const formatPhone = (phone) => {
    if (!phone) return 'N/A';

    // Check for extension (# or x followed by digits)
    const extensionMatch = phone.match(/[#x](\d+)$/i);
    const extension = extensionMatch ? extensionMatch[0] : '';
    const phoneWithoutExt = extensionMatch ? phone.replace(/[#x]\d+$/i, '') : phone;

    // Clean the phone number (remove all non-digits)
    const cleaned = phoneWithoutExt.replace(/\D/g, '');

    // Handle 10-digit US numbers
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}${extension}`;
    }

    // Handle 11-digit numbers (1 + 10 digits, like US country code)
    if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}${extension}`;
    }

    // Handle longer international numbers
    if (cleaned.length > 11) {
      // Format as: +[country code] [rest of number]
      return `+${cleaned.slice(0, cleaned.length - 10)} ${cleaned.slice(-10, -7)}-${cleaned.slice(-7, -4)}-${cleaned.slice(-4)}${extension}`;
    }

    // Return original if it doesn't match expected patterns
    return phone;
  };

// ==================== DEFAULT EXPORT ====================

export default {
  // Timezone
  isIsraelUser,
  getUserTimezone,
  formatDateTimeForUser,

  // Validation
  isValidDate,
  parseDate,

  // API Formats
  toAPIDate,
  toAPIDateTime,
  toLocalDateTime,

  // Display Formats
  formatDate,
  formatLongDate,
  formatTime,
  formatDateWithWeekday,
  formatDateTime,
  formatDateTimeForUser,
  formatShortDate,

  // Comparisons
  isSameDay,
  isToday,
  isPast,
  isFuture,

  // Calculations
  addDays,
  addHours,
  addMinutes,
  addMonths,
  getDurationMinutes,
  formatDuration,

  // Time Parsing
  parseTimeSlot,
  combineDateAndTimeSlot,

  // Date Ranges
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  getWeekDays,

  // Specialized
  toFileNameDate,
  getCurrentTimestamp,
  formatPhone,

  formatCurrency
};
